import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useAuth } from '@/hooks/use-auth';
import { useCurrentUserProfile } from '@/hooks/use-profile';
import { cn } from '@/lib/utils';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router, usePathname } from 'expo-router';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import { DiscordIcon } from '../icons/discord-icon';
import { FlavrlyLogo } from '../icons/flavrly-logo';
import { SidebarNavItem } from './sidebar-nav-item';

export interface SidebarProps {
  unreadCount?: number;
  onCheckIn: () => void;
  onClose?: () => void;
  onNavigate?: () => void;
}

export function Sidebar({ unreadCount, onCheckIn, onClose, onNavigate }: SidebarProps) {
  const theme = useAppTheme();
  const pathname = usePathname();
  const { user: authUser, logout } = useAuth();
  const { data: profileData } = useCurrentUserProfile();

  const user = profileData?.user || authUser;

  const isActive = (route: string) => {
    if (route === '/activity') {
      return pathname === '/' || pathname === '/activity' || pathname.startsWith('/(tabs)/activity');
    }
    return pathname === route || pathname.startsWith(`/(tabs)${route}`) || pathname.startsWith(route);
  };

  const handleNavigate = (route: string) => {
    onNavigate?.();
    router.push(route as any);
  };

  const handleCheckIn = () => {
    onNavigate?.();
    onCheckIn();
  };

  const handleLogout = async () => {
    onNavigate?.();
    await logout();
    router.replace('/(auth)/login');
  };

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
      {onClose && (
        <Pressable
          onPress={onClose}
          accessibilityLabel="Close menu"
          accessibilityRole="button"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            padding: 12,
          }}
        >
          <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
        </Pressable>
      )}
      <View style={{ marginBottom: 8, alignItems: 'center' }}>
        <FlavrlyLogo width={225} captionColor={theme.colors.primary}/>
      </View>

      <Pressable
        onPress={() => Linking.openURL('https://discord.gg/kmvXZsDx4y')}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          marginBottom: 12,
        }}
      >
        <DiscordIcon />
        <Text style={{ color: '#5865F2', fontSize: 12, fontWeight: '600' }}>
          Join the Flavrly Discord
        </Text>
      </Pressable>

      {user && (
        <Pressable
          onPress={() => handleNavigate('/(tabs)/profile')}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 12,
            marginBottom: 8,
          }}
        >
          <UserAvatar
            uri={user.image}
            name={user.name}
            size={40}
            className="border border-border"
          />
          <View style={{ marginLeft: 12, flex: 1 }}>
            <Text
              className="text-sm font-semibold text-foreground"
              numberOfLines={1}
            >
              {user.name || 'User'}
            </Text>
            {user.username && (
              <Text
                className="text-xs text-muted-foreground"
                numberOfLines={1}
              >
                @{user.username}
              </Text>
            )}
          </View>
        </Pressable>
      )}

      <View style={{ marginBottom: 16, width: '100%' }}>
        <SidebarNavItem
          icon="restaurant"
          label="Check In"
          variant="special"
          onPress={handleCheckIn}
        />
      </View>

      <SidebarNavItem
        icon="pulse-outline"
        activeIcon="pulse"
        label="Activity"
        isActive={isActive('/activity')}
        onPress={() => handleNavigate('/(tabs)/activity')}
      />
      <SidebarNavItem
        icon="search-outline"
        activeIcon="search"
        label="Discover"
        isActive={isActive('/discover')}
        onPress={() => handleNavigate('/(tabs)/discover')}
      />
      <SidebarNavItem
        icon="notifications-outline"
        activeIcon="notifications"
        label="Notifications"
        isActive={isActive('/notifications')}
        badge={unreadCount}
        onPress={() => handleNavigate('/(tabs)/notifications')}
      />
      <SidebarNavItem
        icon="bookmark-outline"
        activeIcon="bookmark"
        label="Lists"
        isActive={isActive('/lists')}
        onPress={() => handleNavigate('/(root)/lists')}
      />
      <SidebarNavItem
        icon="trophy-outline"
        activeIcon="trophy"
        label="Leaderboards"
        isActive={isActive('/leaderboard')}
        onPress={() => handleNavigate('/(root)/leaderboard')}
      />

      <View style={{ flex: 1 }} />

      <View className="border-t border-border" style={{ paddingTop: 12 }}>
        {authUser?.role === 'admin' && (
          <SidebarNavItem
            icon="shield-outline"
            activeIcon="shield"
            label="Admin"
            variant="admin"
            // Not using isActive, since we're linking externally
            onPress={() => Linking.openURL('https://admin.flavrly.app')}
          />
        )}
        <SidebarNavItem
          customIcon={
            <Text
              className={cn(
                'text-2xl font-bold',
                isActive('/alpha-program') ? 'text-primary' : 'text-muted-foreground',
              )}
              style={{ lineHeight: 24 }}
            >
              {'\u03B1'}
            </Text>
          }
          label="Alpha Program"
          variant="secondary"
          isActive={isActive('/alpha-program')}
          onPress={() => handleNavigate('/(root)/alpha-program')}
        />
        <SidebarNavItem
          icon="settings-outline"
          activeIcon="settings"
          label="Settings"
          variant="secondary"
          isActive={isActive('/settings')}
          onPress={() => handleNavigate('/(root)/settings')}
        />
        <SidebarNavItem
          icon="log-out-outline"
          label="Logout"
          variant="secondary"
          onPress={handleLogout}
        />
      </View>
    </ScrollView>
  );
}

export { FlavrlyLogo };
